import React, { useState } from "react";
import { AlertTriangle, AlertCircle, Sparkles, XCircle, Eye } from "lucide-react";
import api from "../api/client";
import CTRFixPreview from "./CTRFixPreview";

const severityConfig = {
  critical: {
    icon: AlertTriangle,
    badge: "bg-red-900/20 text-red-400 border-red-800/30",
  },
  warning: {
    icon: AlertCircle,
    badge: "bg-amber-900/20 text-amber-400 border-amber-800/30",
  },
  info: {
    icon: Eye,
    badge: "bg-blue-900/20 text-blue-400 border-blue-800/30",
  },
};

const typeLabels = {
  low_ctr: "Low CTR",
  gap_zone: "Gap Zone",
  declining: "Declining",
  cannibalization: "Cannibalization",
  query_mismatch: "Query Mismatch",
};

export default function CTRInsightsList({ insights, siteId, onUpdate }) {
  const [loading, setLoading] = useState(null);
  const [previewInsight, setPreviewInsight] = useState(null);
  const [error, setError] = useState(null);

  const handleAction = async (insightId, action) => {
    setLoading(insightId);
    try {
      const res = await api.post(
        "/websites/" + siteId + "/gsc/insights/" + insightId + "/action/",
        { action }
      );
      if (action === "generate_fix" && res.data.suggested_fix) {
        setPreviewInsight(res.data);
      }
      if (onUpdate) onUpdate();
    } catch (e) {
      if (e.response && e.response.status === 404) {
        // Insight was recycled by re-analysis — refresh the list
        if (onUpdate) onUpdate();
      } else {
        const msg = e.response?.data?.error || e.message || "Request failed";
        setError({ id: insightId, message: msg });
        setTimeout(() => setError(null), 6000);
      }
    } finally {
      setLoading(null);
    }
  };

  const handleApprove = async () => {
    if (!previewInsight) return;
    await handleAction(previewInsight.id, "approve");
    setPreviewInsight(null);
  };

  const handleReject = async () => {
    if (!previewInsight) return;
    await handleAction(previewInsight.id, "dismiss");
    setPreviewInsight(null);
  };

  if (!insights || !insights.length) {
    return (
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-8 text-center text-gray-500">
        No insights yet. Sync GSC data to generate CTR insights.
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-300">CTR Insights ({insights.length})</h3>
        {insights.map((insight) => {
          const config = severityConfig[insight.severity] || severityConfig.info;
          const Icon = config.icon;
          const isLoading = loading === insight.id;

          return (
            <div key={insight.id} className="bg-dark-300 border border-dark-50 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className={"p-1.5 rounded-lg " + config.badge}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={"text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border " + config.badge}>
                      {typeLabels[insight.insight_type] || insight.insight_type}
                    </span>
                    {insight.source === "bing" && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border bg-blue-900/20 text-blue-400 border-blue-800/30">
                        Bing
                      </span>
                    )}
                    {insight.status !== "pending" && (
                      <span className="text-[10px] uppercase tracking-wider text-gray-500 px-2 py-0.5 rounded bg-dark-50/50">
                        {insight.status.replace("_", " ")}
                      </span>
                    )}
                  </div>
                  <h4 className="text-sm text-gray-200 font-medium mb-1">{insight.title}</h4>
                  <p className="text-xs text-gray-400 line-clamp-2">{insight.description}</p>
                  <div className="text-[10px] text-gray-600 font-mono mt-1">
                    {insight.page_url.replace(/^https?:\/\/[^/]+/, "")}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {insight.status === "pending" && (
                    <>
                      <button
                        onClick={() => handleAction(insight.id, "generate_fix")}
                        disabled={isLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-copper hover:bg-copper-600 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                      >
                        <Sparkles size={12} />
                        {isLoading ? "Generating..." : insight.suggested_fix_type === "content_boost" ? "Generate Content" : "Generate Fix"}
                      </button>
                      <button
                        onClick={() => handleAction(insight.id, "dismiss")}
                        disabled={isLoading}
                        className="p-1.5 text-gray-500 hover:text-gray-300 transition-colors"
                        title="Dismiss"
                      >
                        <XCircle size={16} />
                      </button>
                      {error && error.id === insight.id && (
                        <span className="text-[11px] text-red-400 max-w-[200px] truncate" title={error.message}>
                          {error.message}
                        </span>
                      )}
                    </>
                  )}
                  {insight.status === "fix_generated" && (
                    <button
                      onClick={() => setPreviewInsight(insight)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-copper hover:bg-copper-600 text-white text-xs font-medium rounded-lg transition-colors"
                    >
                      <Eye size={12} />
                      Review Fix
                    </button>
                  )}
                  {(insight.status === "approved" || insight.status === "applied") && (
                    insight.cooldown_days_remaining > 0 ? (
                      <span className="text-[11px] bg-amber-900/20 text-amber-400 border border-amber-800/30 px-2.5 py-1 rounded-lg">
                        Fixed {42 - insight.cooldown_days_remaining}d ago — measuring impact ({insight.cooldown_days_remaining}d remaining)
                      </span>
                    ) : (
                      <span className="text-xs text-green-400">Applied</span>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {previewInsight && (
        <CTRFixPreview
          insight={previewInsight}
          onApprove={handleApprove}
          onReject={handleReject}
          onClose={() => setPreviewInsight(null)}
        />
      )}
    </>
  );
}
